import React, { useState, useEffect, useRef } from "react";
import { Search, Info, AlertTriangle } from "lucide-react";
import api from "../lib/api";

/** SPT-PTE-2026-0014 — inline typeahead for the Receiving/loading screen's
 *  "Issue number" field. Searches EVERY existing issue number in the system
 *  (not scoped to one customer — issue numbers are PTE's own internal
 *  identifier and must stay unique across the whole register) so staff can
 *  see existing variants of a base number (e.g. typing "BK100" surfaces
 *  BK100-1, BK100-2, ...) and pick the correct next one. Informational
 *  only — never blocks saving, just makes existing numbers visible up
 *  front.
 *
 *  SPT-PTE-2026-0018 (2026-08-04): the exact-duplicate heads-up used to be
 *  styled red/"AlertTriangle" with instructional wording ("use a different
 *  or incremented number"), which reception staff read as a hard validation
 *  error and stopped submitting — even though nothing ever actually blocked
 *  the save (returning equipment legitimately reuses its old issue number
 *  on a new delivery note all the time). Restyled to an amber, non-blocking
 *  "heads-up" that explicitly says it's fine to continue and the number
 *  will be linked to the new DN.
 *
 *  SPT-PTE-2026-0049 (2026-08-27): ONE exception to that "never blocks"
 *  rule — if the exact match is currently Unserviceable (or Scrapped), that
 *  is a genuinely different situation (the physical tool may not be usable
 *  at all), so it gets a distinct red banner and requires an explicit
 *  Yes/No click before the caller is allowed to save. `onUnserviceableStatusChange`
 *  reports `null` (nothing to confirm) or `{ ...match, acked }` up to the
 *  parent so it can block the whole form's save button until acked.
 */
const normIssueKey = (s) => (s || "").toString().replace(/[\s-]/g, "").toUpperCase();
const UNSERVICEABLE_STATUSES = new Set(["unserviceable_review"]);
const isUnserviceable = (m) => !!m.scrapped || UNSERVICEABLE_STATUSES.has(m.status) || (m.status || "").startsWith("unserviceable");

export function IssueNumberTypeahead({ value, onChange, onPick, onUnserviceableStatusChange, placeholder, testid, disabled }) {
  const [matches, setMatches] = useState([]);
  const [exactDuplicate, setExactDuplicate] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ack, setAck] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (disabled) { setMatches([]); setExactDuplicate(false); return; }
    const v = (value || "").trim();
    if (v.length < 2) { setMatches([]); setExactDuplicate(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/equipment/issue-number-lookup", { params: { issue_number: v } });
        setMatches(r.data.matches || []);
        setExactDuplicate(!!r.data.exact_duplicate);
      } catch { setMatches([]); setExactDuplicate(false); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [value, disabled]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const v = (value || "").trim();
  const vKey = normIssueKey(v);
  const flaggedMatch = exactDuplicate
    ? matches.find((m) => isUnserviceable(m) && normIssueKey(m.issue_number) === vKey)
    : null;

  // Fresh flagged match (by equipment id) → always re-ask, never carry an old ack forward.
  const flaggedId = flaggedMatch?.id || null;
  const prevFlaggedId = useRef(null);
  useEffect(() => {
    if (flaggedId !== prevFlaggedId.current) {
      prevFlaggedId.current = flaggedId;
      setAck(false);
      onUnserviceableStatusChange && onUnserviceableStatusChange(flaggedId ? { ...flaggedMatch, acked: false } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flaggedId]);

  const confirmUse = () => {
    setAck(true);
    onUnserviceableStatusChange && onUnserviceableStatusChange({ ...flaggedMatch, acked: true });
  };
  const rejectUse = () => {
    setAck(false);
    onUnserviceableStatusChange && onUnserviceableStatusChange(null);
    onChange("");
  };

  const hasResults = open && v.length >= 2 && (loading || matches.length > 0 || !loading);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div className="input" style={{
        display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
        borderColor: flaggedMatch ? "var(--red, #b3261e)" : (exactDuplicate ? "var(--gold-deep, #b8860b)" : undefined),
      }}>
        <Search size={13} color="var(--text-3)" />
        <input
          className="mono"
          style={{ border: "none", outline: "none", width: "100%", background: "none", fontSize: 13 }}
          placeholder={placeholder}
          value={value || ""}
          disabled={disabled}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid={testid}
        />
      </div>
      {flaggedMatch && !ack && (
        <div style={{ color: "var(--red, #b3261e)", background: "var(--red-bg, #fde7e7)", border: "1px solid #f0b8b8",
                       borderRadius: 6, fontSize: 11.5, marginTop: 4, padding: "8px 10px" }}
          data-testid={`${testid}-unserviceable-warning`}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
            <AlertTriangle size={13} style={{ marginTop: 1, flex: "none" }} />
            <span>
              &quot;{v}&quot; is currently on an item marked <b>{(flaggedMatch.status || "scrapped").replace(/_/g, " ")}</b>
              {" "}({flaggedMatch.description || "—"} · {flaggedMatch.customer_name || "—"}). Can this tool still be used?
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn sm" style={{ background: "var(--red, #b3261e)", color: "#fff", border: "none" }}
              onClick={confirmUse} data-testid={`${testid}-unserviceable-confirm`}>
              Yes, use it anyway
            </button>
            <button type="button" className="btn sm ghost" onClick={rejectUse}
              data-testid={`${testid}-unserviceable-reject`}>
              No, let me change it
            </button>
          </div>
        </div>
      )}
      {flaggedMatch && ack && (
        <div style={{ color: "var(--red, #b3261e)", background: "var(--red-bg, #fde7e7)", border: "1px solid #f0b8b8",
                       borderRadius: 6, fontSize: 11, marginTop: 4, padding: "5px 8px", display: "flex",
                       alignItems: "center", gap: 6, justifyContent: "space-between" }}
          data-testid={`${testid}-unserviceable-acked`}>
          <span><AlertTriangle size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />Confirmed to use this {(flaggedMatch.status || "scrapped").replace(/_/g, " ")} item anyway</span>
          <button type="button" style={{ background: "none", border: "none", textDecoration: "underline", color: "inherit", cursor: "pointer", fontSize: 11 }}
            onClick={() => setAck(false)} data-testid={`${testid}-unserviceable-undo`}>change</button>
        </div>
      )}
      {!flaggedMatch && exactDuplicate && (
        <div style={{ color: "#8a5a00", background: "#fff8e6", border: "1px solid #e3d28a",
                       borderRadius: 6, fontSize: 11.5, marginTop: 4, padding: "6px 8px",
                       display: "flex", alignItems: "flex-start", gap: 6 }}
          data-testid={`${testid}-duplicate-warning`}>
          <Info size={12} style={{ marginTop: 1, flex: "none" }} />
          <span>
            Heads-up: &quot;{v}&quot; already exists on {matches.length} record{matches.length !== 1 ? "s" : ""}.
            That&apos;s fine for returning equipment — <b>you can still submit</b>; it will be linked to this
            delivery note. Only change it if this is a genuinely different physical item.
          </span>
        </div>
      )}
      {hasResults && (
        <div className="card" style={{ position: "absolute", zIndex: 20, top: 40, left: 0, right: 0, maxHeight: 260, overflowY: "auto", padding: 4 }}
          data-testid={`${testid}-dropdown`}>
          {loading && <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>Searching…</div>}
          {!loading && matches.length === 0 && (
            <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }} data-testid={`${testid}-no-matches`}>
              No existing issue numbers start with &quot;{v}&quot; — safe to use.
            </div>
          )}
          {!loading && matches.map((m) => (
            // SPT-PTE-2026-0045: rows are now clickable — selecting one fires
            // onPick(m) so the caller can auto-fill the rest of the line item
            // (previously informational-only, with no way to select).
            <button key={m.id} type="button" className="nav-item"
              style={{ borderRadius: 6, padding: "8px 10px", width: "100%", textAlign: "left", border: "none", background: "none", color: "var(--text)", cursor: "pointer" }}
              onClick={() => { onPick && onPick(m); setOpen(false); }}
              data-testid={`${testid}-opt-${m.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                <div style={{ minWidth: 0 }}>
                  <span className="t-strong mono" style={{ fontSize: 13 }}>{m.issue_number}</span>
                  {(m.issue_number || "").toLowerCase() === v.toLowerCase() && (
                    <span className="badge b-gold" style={{ marginLeft: 6, fontSize: 9.5 }}><span className="bd" />Already used — OK to reuse</span>
                  )}
                  <div className="t-mut" style={{ fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.description || "—"} · {m.customer_name || "—"}
                  </div>
                </div>
                <div className="mono t-mut" style={{ fontSize: 10.5, whiteSpace: "nowrap" }}>
                  {m.status ? m.status.replace(/_/g, " ") : "—"}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
